import { requestWithCookieAuth } from "api";
import { DECOODA_API_BASE } from "config";
import { formatExportFilename } from "./components/projects/projectUtils";

export const getFiles = async () => {
  const url = new URL(`${DECOODA_API_BASE}/files/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.json();
};

export const getProjects = async () => {
  const url = new URL(`${DECOODA_API_BASE}/projects/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.json();
};

export const getProject = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.json();
};

export const createProject = async ({
  name,
  access,
  objective,
  clientName,
  workdayCode,
}) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      access,
      objective,
      clientName,
      workdayCode,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const updateProject = async ({
  projectId,
  name,
  access,
  objective,
  clientName,
  workdayCode,
}) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "PUT",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      access,
      objective,
      clientName,
      workdayCode,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const deleteFile = async ({ fileId }) => {
  const response = await requestWithCookieAuth(
    `${DECOODA_API_BASE}/files/${fileId}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "DELETE",
    },
  );

  return await response.json();
};

export const deleteProject = async ({ projectId }) => {
  const response = await requestWithCookieAuth(
    `${DECOODA_API_BASE}/projects/${projectId}/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "DELETE",
    },
  );

  return await response.json();
};

export const getReviewsOfFile = async (fileId) => {
  const url = new URL(`${DECOODA_API_BASE}/files/${fileId}/reviews/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getProjectReviews = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/reviews/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getImpactAnalysisOfFile = async (fileId) => {
  const url = new URL(`${DECOODA_API_BASE}/files/${fileId}/impact/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getTaskStatus = async (taskId) => {
  const url = new URL(`${DECOODA_API_BASE}/atd/${taskId}/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const redoImpactAnalysis = async ({
  fileId,
  threshold,
  dry_run = false,
}) => {
  const url = new URL(
    `${DECOODA_API_BASE}/files/${fileId}/impact/?dry_run=${dry_run}`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ threshold }),
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getSignedUrl = async ({ filename, projectId, fileData }) => {
  const response = await requestWithCookieAuth(
    `${DECOODA_API_BASE}/files/getSignedUrl/`,
    {
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        filename,
        project_id: projectId,
        fileData, // Base64 encoded first 5MB of file for validation
      }),
    },
  );

  return await response.json();
};

export const getDownloadSignedUrl = async ({ filename }) => {
  const response = await requestWithCookieAuth(`${DECOODA_API_BASE}/files/`, {
    redirect: "follow",
    body: JSON.stringify({ filename }),
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  return await response.json();
};

export const runAtd = async ({ runAtdInput }) => {
  const response = await requestWithCookieAuth(`${DECOODA_API_BASE}/atd/`, {
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(runAtdInput),
  });

  return await response.json();
};

export const renameTopic = async ({ fileId, oldName, newName }) => {
  const url = new URL(`${DECOODA_API_BASE}/files/${fileId}/topics/rename/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ old_name: oldName, new_name: newName }),
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error("Failed to rename topic");
  }

  return await response.json();
};

export const mergeTopics = async ({ fileId, sourceTopic, targetTopic }) => {
  const url = new URL(`${DECOODA_API_BASE}/files/${fileId}/topics/merge/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      source_topic: sourceTopic,
      target_topic: targetTopic,
    }),
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error("Failed to merge topics");
  }

  return await response.json();
};

// Project-based topic operations
export const renameTopicInProject = async ({ projectId, oldName, newName }) => {
  const url = new URL(
    `${DECOODA_API_BASE}/projects/${projectId}/topics/rename/`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ old_name: oldName, new_name: newName }),
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error("Failed to rename topic");
  }

  return await response.json();
};

export const mergeTopicsInProject = async ({
  projectId,
  sourceTopic,
  targetTopic,
}) => {
  const url = new URL(
    `${DECOODA_API_BASE}/projects/${projectId}/topics/merge/`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      source_topic: sourceTopic,
      target_topic: targetTopic,
    }),
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error("Failed to merge topics");
  }

  return await response.json();
};

export const exportProjectFile = async ({ fileId, ascooccurrence }) => {
  const response = await requestWithCookieAuth(
    `${DECOODA_API_BASE}/files/${fileId}/exportfile/?ascooccurrence=${ascooccurrence}`,
    {
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = formatExportFilename(undefined, "ProjectResults", "csv");
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]*)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

export const scoreProject = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/score/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getProjectStatus = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/status/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const exportProject = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/export/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = formatExportFilename(undefined, "ProjectResults", "csv");
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]*)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);

  return { success: true, filename };
};

export const addAttachment = async ({
  projectId,
  filename,
  fileUrl,
  delimiter,
  contentColumn,
  columns,
  sheetName,
}) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/attachments/`);

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename,
      fileUrl,
      delimiter,
      contentColumn,
      columns,
      sheetName,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const updateAttachment = async ({
  projectId,
  attachmentId,
  delimiter,
  contentColumn,
  sheetName,
  columns,
}) => {
  const url = new URL(
    `${DECOODA_API_BASE}/projects/${projectId}/attachments/${attachmentId}/`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "PUT",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      delimiter,
      contentColumn,
      sheetName,
      columns,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const deleteAttachment = async ({ projectId, attachmentId }) => {
  const url = new URL(
    `${DECOODA_API_BASE}/projects/${projectId}/attachments/${attachmentId}/`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "DELETE",
    redirect: "follow",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const getProjectImpactAnalysis = async ({ projectId }) => {
  const url = new URL(`${DECOODA_API_BASE}/projects/${projectId}/impact/`);

  const response = await requestWithCookieAuth(url.toString(), {
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};

export const updateProjectImpactAnalysis = async ({
  projectId,
  threshold,
  dryRun = false,
}) => {
  const url = new URL(
    `${DECOODA_API_BASE}/projects/${projectId}/impact/?dry_run=${dryRun}`,
  );

  const response = await requestWithCookieAuth(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ threshold }),
    redirect: "follow",
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return await response.json();
};
